import { isPlainObject, isString } from 'lodash';

export default function prepareStandAloneSyncSnippet(syncInjectorSettings, cdnURL) {
  const { consent, syncs } = syncInjectorSettings;

  const consentString = buildConsentString(consent);
  const integrationsString = buildIntegrationsString(syncs);

  return `
  <script>
  (function() {
    var e = document.createElement('script');
    e.type = 'text/javascript';
    e.async = true;
    e.src = ${quote(cdnURL)};
    var n = document.getElementsByTagName('script')[0];
    n.parentNode.insertBefore(e, n);
    e.onload = function() {
      syncInjector('config', {
        // Replace with unique matchID
        matchID: '',
        consentSettings: ${consentString},
        integrations: ${integrationsString},
      });
      syncInjector('init').then((result) => {
        // Replace with logic to handle result of syncs
      });
    };
  })();
</script>
  `;
}

// Indentation for settings
const BASE_INDENT = 10;

// JSON.stringify escapes backslashes, newlines and control characters; we only
// swap the outer quotes. < is escaped so a value can't close the snippet's
// surrounding script tag.
const quote = value => `'${JSON.stringify(String(value)).slice(1, -1).replace(/'/g, "\\'").replace(/</g, '\\u003c')}'`;

// Keys that aren't valid identifiers (customer-written JSON in TEXT_AREA settings) must be quoted.
const jsKey = key => (/^[A-Za-z_$][\w$]*$/.test(key) ? key : quote(key));

const buildConsentString = consent => {
  const lines = [`explicitConsent: ${consent.explicitConsent}`];

  // A config saved through the consent source UI carries consentSource instead
  // of a top-level cookieName. Interpolating the absent key produced a snippet
  // reading `cookieName: 'undefined'`, which normalizeConsentSettings then took
  // as a real cookie name and read a cookie called "undefined".
  if (consent.cookieName !== undefined) {
    lines.push(`cookieName: ${quote(consent.cookieName)}`);
  }
  // < can only appear inside JSON string literals, so \u003c is safe there (see quote).
  for (const key of ['consentSource', 'optInValues', 'globalPrivacyControl', 'matchIDConsentCode']) {
    if (consent[key] !== undefined) lines.push(`${key}: ${JSON.stringify(consent[key]).replace(/</g, '\\u003c')}`);
  }
  lines.push(`codes: [${consent.codes.map(code => quote(code)).join(',')}]`);

  return `{\n${lines.map(line => `${' '.repeat(BASE_INDENT)}${line}`).join(',\n')}\n${' '.repeat(BASE_INDENT - 2)}}`;
};

const buildIntegrationsString = syncs => {
  return `{${syncs
    .map(sync => {
      return `\n${' '.repeat(BASE_INDENT)}${jsKey(sync.name)}: {${handleSettings(
        sync.settings,
        sync.consentSettings
      )}\n${' '.repeat(BASE_INDENT)}}`;
    })
    .join(',')}\n${' '.repeat(BASE_INDENT - 2)}}`;
};

const handleSettings = (settings, consentSettings) => {
  return Object.keys(settings)
    .map(key => writeSetting(key, settings[key]))
    .concat(
      `\n${' '.repeat(BASE_INDENT + 2)}consentSettings: {\n${' '.repeat(BASE_INDENT + 4)}code: ${quote(
        consentSettings.code
      )}\n${' '.repeat(BASE_INDENT + 2)}}`
    )
    .join(',');
};

const writeSetting = (key, value) => {
  return `\n${' '.repeat(BASE_INDENT + 2)}${jsKey(key)}: ${serializeValue(value, BASE_INDENT + 2)}`;
};

// Object values (e.g. Floodlight/CM360 advertiser arrays) must not coerce to "[object Object]" (sc-39641).
const serializeValue = (value, indent) => {
  if (Array.isArray(value)) {
    return `[${value.map(item => serializeValue(item, indent)).join(', ')}]`;
  }
  if (isPlainObject(value)) {
    const entries = Object.keys(value).map(
      key => `\n${' '.repeat(indent + 2)}${jsKey(key)}: ${serializeValue(value[key], indent + 2)}`
    );
    return `{${entries.join(',')}\n${' '.repeat(indent)}}`;
  }
  if (isString(value)) {
    return quote(value);
  }
  return `${value}`;
};
